import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTelegram } from '@/hooks/useTelegram'
import { useAuthStore } from '@/store/authStore'
import { getCurrentUser } from '@/services/auth'
import Layout from '@/components/layout/Layout'
import LoginPage from '@/pages/auth/LoginPage'
import TrainerDashboard from '@/pages/trainer/DashboardPage'
import TrainerSchedule from '@/pages/trainer/SchedulePage'
import TrainerClients from '@/pages/trainer/ClientsPage'
import TrainerScheduleSettings from '@/pages/trainer/ScheduleSettingsPage'
import ClientDashboard from '@/pages/client/DashboardPage'
import ClientBooking from '@/pages/client/BookingPage'
import ClientMyTrainings from '@/pages/client/MyTrainingsPage'
import ClientJoinTrainer from '@/pages/client/JoinTrainerPage'
import MorePage from '@/pages/MorePage'
import NotFoundPage from '@/pages/NotFoundPage'

function App() {
  const { webApp, isReady } = useTelegram()
  const { user, isAuthenticated, isLoading, accessToken, logout, setAuth } = useAuthStore()
  const [isValidating, setIsValidating] = useState(true)

  // Проверка токена при загрузке приложения
  useEffect(() => {
    const validateToken = async () => {
      // Если есть токен, но нет user - нужно проверить токен
      if (accessToken) {
        try {
          const userData = await getCurrentUser()
          // Токен валиден, обновляем user данные
          setAuth({
            user: userData,
            accessToken,
            refreshToken: useAuthStore.getState().refreshToken || '',
          })
        } catch (error) {
          console.error('Token validation failed:', error)
          // Токен невалиден, выходим
          logout()
        }
      }
      setIsValidating(false)
    }

    validateToken()
  }, [])

  useEffect(() => {
    // Настраиваем Telegram Mini App
    if (webApp) {
      webApp.ready()
      webApp.expand()

      // Устанавливаем цвета темы
      document.documentElement.style.setProperty(
        '--tg-theme-bg-color',
        webApp.themeParams.bg_color || '#ffffff'
      )
      document.documentElement.style.setProperty(
        '--tg-theme-text-color',
        webApp.themeParams.text_color || '#000000'
      )
    }
  }, [webApp])

  // Показываем загрузку
  if (!isReady || isLoading || isValidating) {
    return (
      <div className="flex items-center justify-center h-screen bg-tg-bg">
        <div className="text-tg-hint">Загрузка...</div>
      </div>
    )
  }

  // Если не авторизован - показываем страницу входа
  if (!isAuthenticated || !user) {
    return <LoginPage />
  }

  // Роутинг в зависимости от роли
  const isClient = user.role === 'client'
  const isTrainer = user.role === 'trainer' || user.role === 'admin'

  // Защита маршрутов по ролям
  const TrainerRoute = ({ children }: { children: React.ReactNode }) => {
    return isTrainer ? <>{children}</> : <Navigate to="/" replace />
  }

  const ClientRoute = ({ children }: { children: React.ReactNode }) => {
    return isClient ? <>{children}</> : <Navigate to="/" replace />
  }

  return (
    <Layout>
      <Routes>
        {/* Главная - зависит от роли */}
        <Route
          path="/"
          element={isClient ? <ClientDashboard /> : <TrainerDashboard />}
        />

        {/* Тренер / Админ маршруты */}
        <Route path="/schedule" element={<TrainerRoute><TrainerSchedule /></TrainerRoute>} />
        <Route path="/schedule/settings" element={<TrainerRoute><TrainerScheduleSettings /></TrainerRoute>} />
        <Route path="/clients" element={<TrainerRoute><TrainerClients /></TrainerRoute>} />

        {/* Клиент маршруты */}
        <Route path="/my-trainings" element={<ClientRoute><ClientMyTrainings /></ClientRoute>} />
        <Route path="/booking" element={<ClientRoute><ClientBooking /></ClientRoute>} />
        <Route path="/booking/:trainerId" element={<ClientRoute><ClientBooking /></ClientRoute>} />
        <Route path="/join" element={<ClientRoute><ClientJoinTrainer /></ClientRoute>} />

        {/* Общие маршруты */}
        <Route path="/more" element={<MorePage />} />

        {/* 404 */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Layout>
  )
}

export default App
